/*
  Adds main product + checked upsells in one cart/add when extras are ticked.
  Used by snippets/product-upsells.liquid.
*/

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class ProductUpsells extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.handleModalSelectChange = this.handleModalSelectChange.bind(this);
    this.handleModalKeydown = this.handleModalKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.activeVariantCard = null;
    this.previousFocus = null;
    this.modalWasPortaled = false;
  }

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.errorElement = this.querySelector('[data-upsell-error]');
    this.modal = this.querySelector('[data-upsell-modal]');
    this.modalProduct = this.querySelector('[data-upsell-modal-product]');
    this.modalSelect = this.querySelector('[data-upsell-modal-select]');
    this.modalImage = this.querySelector('[data-upsell-modal-image]');
    this.modalConfirm = this.modal ? this.modal.querySelector('[data-upsell-modal-confirm]') : null;
    this.form = this.findProductForm();

    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      this.modalWasPortaled = true;
    }

    this.addEventListener('change', this.handleChange);

    if (this.modal) {
      this.modal.addEventListener('click', this.handleModalClick);
      document.addEventListener('keydown', this.handleModalKeydown);
    }

    if (this.modalSelect) {
      this.modalSelect.addEventListener('change', this.handleModalSelectChange);
    }

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit, true);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('keydown', this.handleModalKeydown);

    if (this.modal) {
      this.modal.removeEventListener('click', this.handleModalClick);
      if (this.modalWasPortaled && this.modal.parentElement === document.body) {
        this.modal.remove();
      }
    }

    if (this.modalSelect) {
      this.modalSelect.removeEventListener('change', this.handleModalSelectChange);
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit, true);
    }

    document.documentElement.classList.remove('product-upsells-modal-open');
    this.removeAttribute('data-ready');
  }

  findProductForm() {
    const formId = this.getAttribute('data-product-form-id');

    if (formId) {
      const formById = document.getElementById(formId);
      if (formById) return formById;
    }

    const nearbyForm = this.closest('form[action*="/cart/add"]');
    if (nearbyForm) return nearbyForm;

    const section = this.closest('[id^="shopify-section-"], .shopify-section, section');
    if (section) {
      const formInSection = section.querySelector('form[action*="/cart/add"]');
      if (formInSection) return formInSection;
    }

    return document.querySelector('form[action*="/cart/add"]');
  }

  handleChange(event) {
    if (event.target.matches('[data-upsell-checkbox]')) {
      this.handleCheckboxChange(event.target);
    }

    if (event.target.matches('[data-upsell-variant-select]')) {
      this.updateVariant(event.target);
    }
  }

  handleCheckboxChange(checkbox) {
    if (!checkbox.checked) return;

    const card = checkbox.closest('[data-upsell-card]');
    const select = card?.querySelector('[data-upsell-variant-select]');

    if (select) {
      this.openVariantModal(card);
    }
  }

  updateVariant(select) {
    const card = select.closest('.product-upsells__card');
    if (!card) return;

    const checkbox = card.querySelector('[data-upsell-checkbox]');
    const selectedOption = select.options[select.selectedIndex];
    const price = card.querySelector('[data-upsell-price]');
    const image = card.querySelector('.product-upsells__image');

    if (checkbox && selectedOption) {
      checkbox.dataset.variantId = selectedOption.value;
      checkbox.disabled = selectedOption.disabled;
    }

    if (price && selectedOption?.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }

    if (image && selectedOption?.dataset.image) {
      image.src = selectedOption.dataset.image;

      if (selectedOption.dataset.imageSrcset) {
        image.srcset = selectedOption.dataset.imageSrcset;
      } else {
        image.removeAttribute('srcset');
      }

      if (selectedOption.dataset.imageSizes) {
        image.sizes = selectedOption.dataset.imageSizes;
      } else {
        image.removeAttribute('sizes');
      }

      if (selectedOption.dataset.imageAlt) {
        image.alt = selectedOption.dataset.imageAlt;
      }
    }
  }

  openVariantModal(card) {
    const sourceSelect = card.querySelector('[data-upsell-variant-select]');
    if (!this.modal || !this.modalSelect || !sourceSelect) return;

    this.activeVariantCard = card;
    this.previousFocus = document.activeElement;
    this.modalSelect.innerHTML = '';

    Array.from(sourceSelect.options).forEach((option) => {
      this.modalSelect.add(option.cloneNode(true));
    });

    this.modalSelect.value = sourceSelect.value;

    if (this.modalProduct) {
      this.modalProduct.textContent = card.dataset.upsellTitle || '';
    }

    this.updateModalState();

    this.modal.hidden = false;
    document.documentElement.classList.add('product-upsells-modal-open');

    window.requestAnimationFrame(() => {
      this.modalSelect.focus();
    });
  }

  updateModalState() {
    if (!this.modalSelect) return;
    const option = this.modalSelect.options[this.modalSelect.selectedIndex];

    if (this.modalImage) {
      const src = option?.dataset.image;
      if (src) {
        this.modalImage.src = src;
        this.modalImage.alt = option.dataset.imageAlt || '';
        this.modalImage.hidden = false;
      } else {
        this.modalImage.removeAttribute('src');
        this.modalImage.hidden = true;
      }
    }

    if (this.modalConfirm) {
      this.modalConfirm.disabled = !!option?.disabled;
    }
  }

  handleModalSelectChange() {
    this.updateModalState();
  }

  handleModalClick(event) {
    if (event.target.closest('[data-upsell-modal-confirm]')) {
      this.confirmVariantModal();
      return;
    }

    if (event.target.closest('[data-upsell-modal-close], [data-upsell-modal-cancel]')) {
      this.closeVariantModal();
    }
  }

  confirmVariantModal() {
    const sourceSelect = this.activeVariantCard?.querySelector('[data-upsell-variant-select]');
    if (sourceSelect && this.modalSelect) {
      sourceSelect.value = this.modalSelect.value;
      this.updateVariant(sourceSelect);
    }

    this.closeVariantModal({ keepChecked: true });
  }

  closeVariantModal(options = {}) {
    const { keepChecked = false } = options;
    const checkbox = this.activeVariantCard?.querySelector('[data-upsell-checkbox]');

    if (!keepChecked && checkbox) {
      checkbox.checked = false;
    }

    if (this.modal) {
      this.modal.hidden = true;
    }

    document.documentElement.classList.remove('product-upsells-modal-open');

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.activeVariantCard = null;
    this.previousFocus = null;
  }

  handleModalKeydown(event) {
    if (!this.modal || this.modal.hidden) return;

    if (event.key === 'Escape') {
      this.closeVariantModal();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusables = this.getModalFocusables();
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const insideModal = this.modal.contains(active);

    if (event.shiftKey) {
      if (!insideModal || active === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!insideModal || active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getModalFocusables() {
    if (!this.modal) return [];
    return Array.from(this.modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('hidden') && el.offsetParent !== null,
    );
  }

  selectedItems() {
    return Array.from(this.querySelectorAll('[data-upsell-checkbox]:checked:not(:disabled)'))
      .map((checkbox) => ({
        id: Number(checkbox.dataset.variantId),
        quantity: 1,
      }))
      .filter((item) => item.id);
  }

  /**
   * When extras are ticked: one multi-item cart/add (main + upsells), then drawer.
   * Unticked path falls through to product-form.
   * @param {Event} event - Submit event.
   */
  async handleSubmit(event) {
    const upsellItems = this.selectedItems();
    if (!upsellItems.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setError('');

    const productFormEl = this.form.closest('product-form');
    const submitter = event.submitter
      || (productFormEl && productFormEl.submitBtn)
      || this.form.querySelector('[name="add"], [type="submit"]');

    // Paint loading / open drawer immediately (INP response).
    if (submitter) {
      submitter.setAttribute('aria-disabled', 'true');
      submitter.classList.add('is-loading');
    }
    this.setBusy(submitter, true);

    const cartDrawer = document.querySelector('cart-drawer');
    const quickAddDrawer = this.form.closest('quick-add-drawer');
    const shouldOpenDrawer = cartDrawer && !quickAddDrawer && theme.settings.afterAtc === 'drawer';
    if (shouldOpenDrawer) {
      cartDrawer.open(submitter);
    }

    try {
      const data = await this.addBundleToCart(upsellItems, cartDrawer);
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      if (theme.settings.afterAtc === 'page') {
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      } else {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }
        if (quickAddDrawer) quickAddDrawer.addedToCart();
        if (cartDrawer) cartDrawer.renderContents(data, false);
        if (productFormEl && typeof productFormEl.dispatchCartEvents === 'function') {
          productFormEl.dispatchCartEvents(data);
        }
      }
    } catch (error) {
      this.setError(error.message || 'Unable to add selected extras. Please try again.');
      if (cartDrawer) cartDrawer.refresh(true);
    } finally {
      this.setBusy(submitter, false);
      if (submitter) {
        submitter.classList.add('is-success');
        submitter.removeAttribute('aria-disabled');
        setTimeout(() => {
          submitter.classList.remove('is-loading');
          submitter.classList.remove('is-success');
        }, 1400);
      }
    }
  }

  /**
   * Single cart/add for main product + checked upsells (atomic bundle).
   * @param {Array<{id: number, quantity: number}>} upsellItems - Checked extras.
   * @param {Element|null} cartDrawer - Cart drawer element if present.
   * @returns {Promise<object>} Cart add JSON (with sections when requested).
   */
  async addBundleToCart(upsellItems, cartDrawer) {
    const idInput = this.form.querySelector('[name="id"]');
    const qtyInput = this.form.querySelector('[name="quantity"]');
    const mainId = idInput ? Number(idInput.value) : 0;
    const mainQty = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;

    if (!mainId) {
      throw new Error('Please select a product option.');
    }

    const formData = new FormData();
    formData.append('items[0][id]', String(mainId));
    formData.append('items[0][quantity]', String(mainQty));

    upsellItems.forEach((item, index) => {
      const i = index + 1;
      formData.append(`items[${i}][id]`, String(item.id));
      formData.append(`items[${i}][quantity]`, String(item.quantity || 1));
    });

    // Preserve other form fields (selling plan, properties, etc.) except id/quantity.
    new FormData(this.form).forEach((value, key) => {
      if (key === 'id' || key === 'quantity' || key.startsWith('items[')) return;
      formData.append(key, value);
    });

    let sections = 'cart-icon-bubble';
    if (cartDrawer) {
      const sectionEl = cartDrawer.closest('.shopify-section');
      if (sectionEl && sectionEl.id) {
        sections += `,${sectionEl.id.replace('shopify-section-', '')}`;
      }
    }
    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sections);

    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const data = await response.json();
    let message = typeof data.description === 'string' ? data.description : data.message;
    if (data.errors && typeof data.errors === 'object') {
      message = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
    }

    if (data.status || !response.ok) {
      throw new Error(message || 'Unable to add selected extras. Please try again.');
    }

    return data;
  }

  setBusy(submitter, isBusy) {
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (submitter) {
      submitter.disabled = isBusy;
    }
  }

  setError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }
}

if (!customElements.get('product-upsells')) {
  customElements.define('product-upsells', ProductUpsells);
}